<template>
  <div>
    <div class="calendar-container">
      <div>
        <div
          class="align-items-center justify-content-between flex-wrap !hidden md:flex"
        >
          <FullCalendarZoom v-model="zoom" />
          <div class="calendar-last-updated-at text-body">
            <b>Last time updated:</b> {{ lastUpdateLabel }}
          </div>
        </div>
        <FullCalendarTimeline
          ref="calendar"
          :options="fullCalendarOptions"
          :class-names="['orders-calendar', { 'mobile-calendar': isMobile }]"
          pre-work-enabled
          :zoom="zoom"
          @open-edit-modal="openModal"
          @refetch-events="refetchEvents"
        />
      </div>
    </div>
    <span class="shortcut-label">Use shortcut Alt + R to reload data</span>
    <edit-appointment-modal
      v-if="editId"
      :appointment-id="editId"
      @hide-modal="closeModal"
    ></edit-appointment-modal>
    <pinned-reason-modal
      v-if="pinToTechId"
      :technician-name="pinToTechName"
      :technician-id="pinToTechId"
      :appointment-id="pinApptId"
      @hide-modal="handlePinnedModalClose"
    ></pinned-reason-modal>
  </div>
</template>

<script>
import moment from "moment-timezone";
import { mapActions, mapGetters, mapState } from "vuex";
import { EventBus } from "@/lib/event_bus.js";
import { isMobileScreenSize } from "@/lib/helpers.js";
import { getQueryVariables } from "@/lib/url.js";

import CONSTANTS from "@/Constants";
import EditAppointmentModal from "@/EditAppointmentModal/EditAppointmentModal.vue";
import { currentTenantTimezone } from "../lib/helpers.js";
import FullCalendarTimeline from "./FullCalendarTimeline.vue";
import FullCalendarZoom from "./FullCalendarZoom.vue";
import PinnedReasonModal from "./PinnedReasonModal.vue";
const { SCHEDULE_DEFAULT_ZOOM } = CONSTANTS;

const PLANNER_REFETCH_DELAY = 5 * 1000;
const CALENDAR_REFRESH_INTERVAL = 60 * 1000;

export default {
  components: {
    FullCalendarTimeline,
    EditAppointmentModal,
    PinnedReasonModal,
    FullCalendarZoom
  },
  data() {
    return {
      calendar: null,
      timezone: currentTenantTimezone,
      updatedAt: new Date(),
      plannerTimeout: null,
      calendarInterval: null,
      editId: null,
      pinToTechId: null,
      pinApptId: null,
      pinToTechName: "",
      zoom: SCHEDULE_DEFAULT_ZOOM
    };
  },
  computed: {
    ...mapState(["tenant", "fetchingTenant"]),
    ...mapGetters(["featureEnabledForTenant"]),
    ...mapState({
      department_ids({ scheduleBoardFilters }) {
        return scheduleBoardFilters.department_ids;
      },
      start({ scheduleBoardFilters }) {
        return scheduleBoardFilters.start;
      },
      fullCalendarOptions() {
        const { start } = getQueryVariables();
        // Pull cached filters
        let cachedFilters = localStorage.getItem("scheduleBoardFilters");
        cachedFilters = cachedFilters ? JSON.parse(cachedFilters) : {};
        const initialDate = start || cachedFilters.start || new Date();
        return {
          initialDate,
          timeZone: this.timezone,
          resources: $.debounce(100, (fetchInfo, successCallback) => {
            return this.fetchData(
              successCallback,
              fetchInfo.start,
              fetchInfo.end,
              "appointments_calendar/resources.json"
            );
          }),
          events: $.debounce(50, (fetchInfo, successCallback) => {
            return this.fetchData(
              successCallback,
              fetchInfo.start,
              fetchInfo.end,
              "appointments_calendar/events.json",
              "&status=all",
              "events"
            );
          }),
          eventDrop: this.handleEventDrop
        };
      },
      lastUpdateLabel() {
        return moment(this.updatedAt).format("h:mm:ss a");
      },
      isMobile() {
        return isMobileScreenSize();
      }
    })
  },
  watch: {
    department_ids(newValue, oldValue) {
      if (newValue !== oldValue) {
        this.$refs.calendar.api.refetchResources();
        this.refetchEvents();
      }
    }
  },
  created() {
    if (!this.tenant && !this.fetchingTenant) {
      this.fetchTenant();
    }
  },
  mounted() {
    const { appointment_id } = getQueryVariables();
    if (appointment_id) {
      this.openModal(appointment_id);
    }
    this.pollData();
    this.refetchEvents = this.refetchEvents.bind(this);
    document.addEventListener("keydown", this.handleKeyboardShortcut);
    EventBus.$on("refresh-schedule-board", this.refetchEvents);
    EventBus.$on("schedule-board-date-change", this.handleDateChange);
  },
  beforeUnmount() {
    document.removeEventListener("keydown", this.handleKeyboardShortcut);
    EventBus.$off("refresh-schedule-board", this.refetchEvents);
    EventBus.$off("schedule-board-date-change", this.handleDateChange);
  },
  methods: {
    ...mapActions(["assignDraggedAppointment", "fetchTenant"]),
    handleDateChange(date) {
      this.$refs.calendar.api.gotoDate(date);
    },
    handleKeyboardShortcut(e) {
      // Alt + r to refetch events
      if (e.altKey && e.which === 82) {
        this.refetchEvents();
      }
    },
    fetchData(callback, start, end, url, additionalParams, resultKey) {
      const { department_ids } = getQueryVariables();
      let queryParams = `?start=${moment(start).format()}&end=${moment(
        end
      ).format()}&_=${Date.now()}`;
      if (department_ids) {
        queryParams = `${queryParams}&department_ids=${department_ids}`;
      }
      if (additionalParams) {
        queryParams += additionalParams;
      }
      return $.ajax("" + url + queryParams).then((res) => {
        this.updatedAt = new Date();
        if (callback) {
          const data = resultKey ? res[resultKey] : res;
          return callback(data);
        }
      });
    },
    handleEventDrop(info) {
      // Hide all tooltips
      tippy.hideAll();
      const oldResource = info.oldResource;
      const newResource = info.newResource;
      const { service_category } = info.event.extendedProps;

      // Ignore null resources
      if (oldResource && newResource) {
        const { department: oldDept } = oldResource.extendedProps;
        const { department: newDept } = newResource.extendedProps;
        const toUnassigned = !newDept.id;

        if (oldResource.id !== newResource.id) {
          const newTechId = parseInt(newResource.id, 10) || null;
          const newTechName = newResource.title;
          if (toUnassigned) {
            // No pinned reason required
            this.assignDraggedAppointment({
              appointmentId: info.event.id,
              technician_profile_id: newTechId,
              pinned_reason: ""
            }).then(() => this.refetchEvents());
          } else {
            // Set tech name, id and appt to open pinned reason modal
            this.pinToTechName = newTechName;
            this.pinToTechId = newTechId;
            this.pinApptId = info.event.id;
          }
        } else {
          // Reset UI after invalid move
          this.refetchEvents();
        }
      }
    },
    refetchEvents() {
      if (this.$refs.calendar) {
        this.$refs.calendar.api.refetchEvents();
        this.updatedAt = new Date();
      }
    },
    waitForPlannerUpdates() {
      // Refetch events after planner updates
      clearTimeout(this.plannerTimeout);
      this.plannerTimeout = setTimeout(
        () => this.refetchEvents(),
        PLANNER_REFETCH_DELAY
      );
    },
    openModal(id) {
      try {
        this.editId = parseInt(id, 10);
      } catch (e) {
        console.error(e);
      }
    },
    closeModal() {
      this.editId = null;
      this.refetchEvents();
    },
    pollData() {
      clearInterval(this.calendarInterval);
      this.calendarInterval = setInterval(() => {
        this.refetchEvents();
      }, CALENDAR_REFRESH_INTERVAL);
    },
    handlePinnedModalClose() {
      this.pinToTechName = null;
      this.pinToTechId = null;
      this.pinApptId = null;
      this.refetchEvents();
    }
  }
};
</script>

<style lang="scss" scoped>
.shortcut-label {
  font-style: italic;
  margin-top: 10px;
  color: var(--gray);
  font-size: 0.8em;
  display: inline-block;
  position: absolute;
  bottom: -20px;
}
.card {
  padding: 15px;
}

.calendar-container {
  position: relative;
  padding-top: 0 !important;
  margin-top: -20px;
  .calendar-last-updated-at {
    color: var(--font-body-color);
    font-size: 0.8em;
    height: 28px;
  }
}

@media (max-width: 600px) {
  .calendar-last-updated-at {
    left: 18px;
    top: 0px !important;
    font-size: 0.8em !important;
  }
}
</style>
